/**
 * Accounts, sessions and the provider contract.
 *
 * ## Why this is an interface
 *
 * Authentication is the one part of an education platform where a home-made
 * implementation is actively dangerous, and §21 of the product brief says so:
 * use a proven provider rather than storing passwords yourself. So this package
 * defines the *contract* and the flows — validation, account states, activation,
 * consent — and leaves credential handling to whatever implements it.
 *
 * `LocalAuthProvider` (see `local.ts`) implements this for development and
 * deliberately never stores a password. A real deployment supplies an adapter
 * over Clerk, Supabase Auth or Auth.js; nothing above this package changes.
 */

import type { AgeBand } from '@yuan-math/learning';

/**
 * Where an account is in its lifecycle.
 *
 * `pending` accounts exist but cannot sign in. That gap is the entire point of
 * activation: it proves the address is reachable by whoever owns it.
 */
export type AccountStatus = 'pending' | 'active' | 'suspended';

/** How the account was created — the platform treats children differently. */
export type AccountKind =
  /** An adult or older student registering for themselves. */
  | 'self'
  /** A child under the consent age; requires a guardian (§29). */
  | 'child'
  /** A parent or guardian, who may hold child profiles. */
  | 'guardian';

export type Account = {
  id: string;
  email: string;
  kind: AccountKind;
  status: AccountStatus;
  createdAt: number;
  activatedAt?: number;
  /** The guardian's address for a child account. Never the child's own. */
  guardianEmail?: string;
  /** Set when a guardian has confirmed consent for a child account. */
  consentGrantedAt?: number;
  /** Learner profile ids this account owns. A family holds several. */
  profileIds: string[];
  /**
   * What the registrant said about themselves, carried for one purpose: to
   * build their learner profile on a device that has never seen it.
   *
   * Display-only, and optional because an account may predate them. Nothing is
   * authorised by either field, which is why they may come from metadata the
   * account holder can write — the same latitude `guardianEmail` has, and for
   * the same reason.
   */
  displayName?: string;
  ageBand?: AgeBand;
};

/**
 * An activation token.
 *
 * In production the token is emailed and never returned to the caller. The
 * development provider returns it so the flow can be walked without a mail
 * server — which is exactly why that provider must not be deployed.
 */
export type ActivationToken = {
  token: string;
  accountId: string;
  expiresAt: number;
  /** Guards against an attacker requesting tokens indefinitely. */
  attempts: number;
};

export const ACTIVATION_TTL_MS = 24 * 60 * 60 * 1000;
export const MAX_ACTIVATION_ATTEMPTS = 5;

export type Session = {
  accountId: string;
  /** Opaque to everything above this package. */
  token: string;
  issuedAt: number;
  expiresAt: number;
};

export const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type RegistrationInput = {
  email: string;
  password: string;
  displayName: string;
  ageBand: AgeBand;
  /** Required when the registrant is a child (§29). */
  guardianEmail?: string;
  /** Must be explicitly true; never defaulted. */
  acceptedTerms: boolean;
};

/** Everything a failure needs to say, in a form the UI can render. */
export type AuthErrorCode =
  | 'invalid-email'
  | 'weak-password'
  | 'invalid-name'
  | 'email-taken'
  | 'terms-not-accepted'
  | 'guardian-required'
  | 'guardian-same-as-child'
  | 'account-not-found'
  | 'not-activated'
  | 'already-activated'
  | 'invalid-token'
  | 'token-expired'
  | 'too-many-attempts'
  | 'invalid-credentials'
  | 'consent-required'
  | 'session-expired'
  | 'same-password'
  | 'not-supported';

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    message: string,
    /** Which form field to attach the message to, when there is one. */
    readonly field?: 'email' | 'password' | 'displayName' | 'guardianEmail' | 'terms',
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Why the guardian's email did or did not go out.
 *
 * The reason is a code so tests and logs can name it; the message is the
 * sentence the learner reads. Both matter — collapsing every failure into one
 * boolean is how a deployed, working mail server came to report itself as
 * switched off.
 */
export type ConsentEmailReason =
  /** No provider, or the function says its Resend key is missing. */
  | 'not-configured'
  /** The request never got an answer — offline, blocked, or a CORS refusal. */
  | 'unreachable'
  /** The session is gone or was never established. */
  | 'not-signed-in'
  /** Signed in, but there is no account row to read a guardian from. */
  | 'account-not-found'
  /** Not a child account, so nobody needs to approve anything. */
  | 'not-needed'
  /** A child account with no guardian address recorded. */
  | 'no-guardian'
  /** Already approved; sending again would confuse the guardian. */
  | 'already-granted'
  /** The mail provider rejected the message. */
  | 'send-failed'
  /** Something else. The server's own words are in `message`. */
  | 'unknown';

export type ConsentEmailResult = {
  sent: boolean;
  /** A sentence written for the learner, ready to display as-is. */
  message: string;
  /** Absent when `sent` is true and there is nothing to explain. */
  reason?: ConsentEmailReason;
};

export type RegistrationResult = {
  account: Account;
  /**
   * Present only when the provider cannot send email itself. A production
   * provider returns `undefined` here and delivers the token out of band.
   */
  developmentToken?: string;
};

/**
 * The contract a real authentication backend must satisfy.
 *
 * Every method is async so that a network-backed provider is a drop-in
 * replacement without any caller changing shape.
 */
export type AuthProvider = {
  /** Human-readable, shown in the account UI so nobody is misled about what is running. */
  readonly name: string;
  /** False for the development stub. The UI must warn when this is false. */
  readonly isSecure: boolean;

  register(input: RegistrationInput, now: number): Promise<RegistrationResult>;
  activate(token: string, now: number): Promise<Account>;
  resendActivation(email: string, now: number): Promise<RegistrationResult>;
  /** A guardian confirming consent for a child account (§29). */
  grantConsent(token: string, now: number): Promise<Account>;
  /**
   * Asks the server to email the guardian.
   *
   * Never rejects: a missing mail server must not block a lesson. It reports
   * *why* it failed instead, because "we could not send it" and "email is
   * switched off here" are different problems with different fixes, and a
   * learner told the wrong one has no way to get unstuck.
   */
  requestGuardianConsent?(): Promise<ConsentEmailResult>;

  /**
   * Starts a password reset.
   *
   * Resolves the same way whether or not the address is registered — telling a
   * stranger which emails have accounts is an information leak, and this is the
   * one endpoint anyone can call without credentials.
   */
  requestPasswordReset(email: string): Promise<void>;
  /**
   * Completes a reset from the code in the emailed link.
   *
   * The code establishes a short-lived session, which is what authorises the
   * change — the old password is never required, because the person resetting
   * is precisely the person who does not have it.
   */
  resetPassword(code: string, newPassword: string): Promise<Account>;

  signIn(email: string, password: string, now: number): Promise<{ account: Account; session: Session }>;
  signOut(session: Session): Promise<void>;
  /** Returns null when the session is missing, expired or revoked. */
  resolveSession(token: string, now: number): Promise<Account | null>;

  /**
   * Reports a session that appeared or went away without being asked for.
   *
   * Not every sign-in comes through `signIn`. An emailed activation or recovery
   * link establishes a session by itself, and so does another tab signing out.
   * Without this the application only learns who it is talking to at mount, so
   * somebody who has just followed their activation link is shown a page
   * offering to sign them in — which is precisely what they did.
   *
   * Returns an unsubscribe. Optional: a provider that cannot be surprised
   * simply does not implement it.
   */
  onSessionChange?(listener: (account: Account | null) => void): () => void;

  updateEmail(accountId: string, email: string, now: number): Promise<Account>;
  attachProfile(accountId: string, profileId: string): Promise<Account>;
  detachProfile(accountId: string, profileId: string): Promise<Account>;
  /** Irreversible, and must remove everything (§29). */
  deleteAccount(accountId: string): Promise<void>;
};
